import React, { useState } from "react";
import { Skill } from "../types";
import { useProfile } from "../context/ProfileContext";

type Props = {
  skills: Skill[];
};

export default function Skills({ skills }: Props) {
  const [showOld, setShowOld] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const { profile, isSkillRelevant } = useProfile();

  const categories = skills.filter((s) => !s.break && s.type);

  return (
    <section>
      <h2 className="section-label mb-6">Compétences</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {categories.map((category) => {
          const regular = category.items?.find((i) => i.level === "REGULAR")?.items || [];
          const sometimes = category.items?.find((i) => i.level === "SOMETIMES")?.items || [];
          const old = category.items?.find((i) => i.level === "OLD")?.items || [];

          const allVisible = [...regular, ...sometimes];
          const allWithOld = [...allVisible, ...old];

          const relevantCount =
            profile === "all"
              ? allVisible.length
              : allVisible.filter((s) => isSkillRelevant(s)).length;

          if (profile !== "all" && relevantCount === 0 && !old.some(isSkillRelevant)) return null;

          const isExpanded = expandedCategory === category.type;

          return (
            <div
              key={category.type}
              className="glass-card p-4 print-avoid-break cursor-pointer hover:border-neutral-300 dark:hover:border-white/10 transition-all duration-300"
              onClick={() =>
                setExpandedCategory(isExpanded ? null : category.type!)
              }
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
                  {category.type}
                </h3>
                <span className="text-[10px] text-neutral-400 dark:text-neutral-500 font-medium tabular-nums">
                  {relevantCount} / {allVisible.length}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {regular
                  .sort((a, b) => a.localeCompare(b))
                  .map((item) => (
                    <span
                      key={item}
                      className={`skill-chip ${
                        profile !== "all" && isSkillRelevant(item)
                          ? "skill-chip-highlight"
                          : ""
                      } ${
                        profile !== "all" && !isSkillRelevant(item) ? "opacity-30" : ""
                      }`}
                    >
                      {item}
                    </span>
                  ))}
                {sometimes
                  .sort((a, b) => a.localeCompare(b))
                  .map((item) => (
                    <span
                      key={item}
                      className={`skill-chip skill-chip-outline ${
                        profile !== "all" && isSkillRelevant(item)
                          ? "skill-chip-highlight !border-transparent"
                          : ""
                      } ${
                        profile !== "all" && !isSkillRelevant(item) ? "opacity-30" : ""
                      }`}
                    >
                      {item}
                    </span>
                  ))}
                {(showOld || isExpanded) &&
                  old.length > 0 &&
                  old
                    .sort((a, b) => a.localeCompare(b))
                    .map((item) => (
                      <span
                        key={item}
                        className={`skill-chip opacity-40 line-through ${
                          profile !== "all" && !isSkillRelevant(item) ? "opacity-10" : ""
                        }`}
                      >
                        {item}
                      </span>
                    ))}
              </div>

              {old.length > 0 && !showOld && !isExpanded && (
                <p className="mt-2 text-[10px] text-neutral-400 dark:text-neutral-500">
                  +{old.length} anciennes
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex gap-5 text-[11px] text-neutral-400 dark:text-neutral-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-2 rounded-sm bg-neutral-800 dark:bg-neutral-200" />
            Régulière
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-2 rounded-sm bg-neutral-100 dark:bg-transparent border border-neutral-200 dark:border-white/[0.1]" />
            Occasionnelle
          </span>
        </div>
        <button
          onClick={() => setShowOld(!showOld)}
          className="no-print text-[11px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors font-medium"
        >
          {showOld ? "Masquer anciennes" : "Afficher anciennes"}
        </button>
      </div>
    </section>
  );
}
